import { AppContext } from 'contexts/AppContext';
import { useContext, useEffect, useMemo, useState } from 'react';

// Users to exclude from statistics (test users, internal testing accounts, etc.)
const EXCLUDED_USERS = new Set([
  'iris.cheng@srsdistribution.com',
  'cristin.connerney@srsdistribution.com',
  'heritagetesting01+invoice@gmail.com',
  'heritagetesting01+orders@gmail.com',
  'anirudh.nori@srsdistribution.com',
  'kedan.zha@srsdistribution.com'
]);

/**
 * Custom hook for processing and aggregating monitoring dashboard data
 * Focuses on data processing and derived state from raw data provided by AppContext
 * - Statistics calculation (Total Users, Avg Questions, Response Time)
 * - Chart data processing (Daily Message Counts, Daily User Counts)
 * - Pie chart data (Query Categories)
 * - Feedback data processing
 */
export const useMonitoringData = () => {
  const {
    messagesData,
    userData,
    dateRange,
    selectedClient,
    activeTab,
    accountTypeFilter
  } = useContext(AppContext);

  // Processed Statistics Data
  const [statisticsData, setStatisticsData] = useState({
    totalUsers: 0,
    avgQuestionsPerUser: '0',
    avgResponseTime: '0',
    internalUsers: 0,
    externalEnglishUsers: 0,
    externalSpanishUsers: 0,
    externalBilingualUsers: 0,
    testingUsers: 0,
    internalMessages: 0,
    externalEnglishMessages: 0,
    externalSpanishMessages: 0,
    externalBilingualMessages: 0,
    testingMessages: 0,
    avgQuestionsPerInternal: '0',
    avgQuestionsPerExternalEnglish: '0',
    avgQuestionsPerExternalSpanish: '0',
    avgQuestionsPerExternalBilingual: '0',
    avgQuestionsPerTesting: '0',
    avgResponseTimeInternal: '0',
    avgResponseTimeExternalEnglish: '0',
    avgResponseTimeExternalSpanish: '0',
    avgResponseTimeExternalBilingual: '0',
    avgResponseTimeTesting: '0'
  });

  // Processed Chart Data
  const [dailyMessageCounts, setDailyMessageCounts] = useState([]);
  const [queryCategories, setQueryCategories] = useState([]);
  const [dailyUserCounts, setDailyUserCounts] = useState([]);

  // Processed Feedback Data
  const [feedbackData, setFeedbackData] = useState({
    total: 0,
    positive: 0,
    negative: 0
  });

  // Processing states
  const [processing, setProcessing] = useState({
    statistics: false,
    dailyMessages: false,
    queryCategories: false,
    feedback: false,
    dailyUsers: false
  });

  // Helper function to determine if user is testing user
  const isTestingUser = (item) => {
    const userId = item.user_id || item.email || item.id;
    if (!userId) return false;

    // Convert to string to ensure we can use string methods
    const userIdStr = String(userId);

    // Check if in excluded users list
    if (EXCLUDED_USERS.has(userIdStr)) return true;

    // Check if email contains 'instalily' or 'photon'
    const lowerUserId = userIdStr.toLowerCase();
    if (lowerUserId.includes('instalily') || lowerUserId.includes('photon')) return true;

    // Check if backend marked as testing
    const currentAccountType = item.account_type || item.account_type;
    if (currentAccountType && currentAccountType.toLowerCase() === 'testing') return true;

    return false;
  };

  // Helper function to determine external user language subtype
  const determineExternalLanguageType = (item) => {
    const userLanguages = item.user_languages || [];
    const chatLanguage = item.chat_language; // Fallback for messages data

    // Use user_languages if available (from users endpoint)
    if (Array.isArray(userLanguages) && userLanguages.length > 0) {
      const hasEnglish = userLanguages.includes('en');
      const hasSpanish = userLanguages.includes('es');

      if (hasEnglish && hasSpanish) {
        return 'External (English & Spanish)';
      } else if (hasSpanish) {
        return 'External (Spanish)';
      } else {
        // Default to English for any other language or English
        return 'External (English)';
      }
    }

    // Fallback to chat_language for messages data or when user_languages is empty
    if (chatLanguage === 'es') {
      return 'External (Spanish)';
    } else if (chatLanguage === 'en') {
      return 'External (English)';
    } else {
      // For null/undefined/other values, default to English
      return 'External (English)';
    }
  };

  // Helper function to determine account type based on user languages and current account type
  const determineAccountType = (item, accountTypeField = 'account_type') => {
    const currentAccountType = item[accountTypeField];

    // Debug logging to see what we're working with
    const itemId = item.id || item.user_id || 'unknown';
    const itemIdStr = String(itemId);
    if (Math.random() < 0.01 || itemIdStr.toLowerCase().includes('michael.johnson')) { // Log ~1% of items or specific user
      console.log('Debug - Account Type Determination:', {
        item_id: itemId,
        user_languages: item.user_languages,
        chat_language: item.chat_language,
        account_type: currentAccountType,
        accountTypeField: accountTypeField,
        available_fields: Object.keys(item)
      });
    }

    // Check if this is a testing user first
    if (isTestingUser(item)) {
      return 'Testing';
    }

    // If backend provided account type, use it directly for internal/external/testing
    if (currentAccountType) {
      const lowerAccountType = currentAccountType.toLowerCase();
      if (lowerAccountType === 'internal') {
        return 'Internal';
      }
      if (lowerAccountType === 'external') {
        // For external users, determine language-based subtype
        return determineExternalLanguageType(item);
      }
      if (lowerAccountType === 'testing') {
        return 'Testing';
      }
    }

    // Fallback: determine account type based on language when backend doesn't provide it
    // (This handles legacy data or cases where backend doesn't set account_type)
    return determineExternalLanguageType(item);
  };

  // Helper function to filter data for analysis (excludes testing users)
  const filterForAnalysis = (data, accountTypeField = 'account_type') => {
    const validData = data.filter(item => {
      const userId = item.user_id || item.email || item.id;

      // Skip if no user ID
      if (!userId) return false;

      // Convert to string and only include users with valid email format (contains @)
      const userIdStr = String(userId);
      if (!userIdStr.includes('@')) return false;

      // Skip testing users for analysis
      if (isTestingUser(item)) return false;

      return true;
    });

    // Then apply account type filter if specified
    if (!accountTypeFilter) return validData;
    return validData.filter(item => {
      const accountType = determineAccountType(item, accountTypeField);

      // Handle bilingual users - they appear in both English and Spanish filters
      if (accountType === 'External (English & Spanish)') {
        return accountTypeFilter === 'External (English)' || accountTypeFilter === 'External (Spanish)';
      }

      return accountType === accountTypeFilter;
    });
  };

  // Helper function to filter data for display (excludes testing users and filters by account type)
  const filterForDisplay = (data, accountTypeField = 'account_type') => {
    const validData = data.filter(item => {
      const userId = item.user_id || item.email || item.id;

      // Skip if no user ID
      if (!userId) return false;

      // Convert to string and only include users with valid email format (contains @)
      const userIdStr = String(userId);
      if (!userIdStr.includes('@')) return false;

      // Skip testing users for display as well
      if (isTestingUser(item)) return false;

      return true;
    });

    // Apply account type filter if specified
    if (!accountTypeFilter) return validData;
    return validData.filter(item => {
      const accountType = determineAccountType(item, accountTypeField);

      // Handle bilingual users - they appear in both English and Spanish filters
      if (accountType === 'External (English & Spanish)') {
        return accountTypeFilter === 'External (English)' || accountTypeFilter === 'External (Spanish)';
      }

      return accountType === accountTypeFilter;
    });
  };

  // Helper to build YYYY-MM-DD using UTC date parts to match backend timezone handling
  const formatLocalDateKey = (date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Process statistics from raw data
  useEffect(() => {
    if (!messagesData || !userData) return;

    setProcessing(prev => ({ ...prev, statistics: true }));

    try {
      console.log('Statistics calculation using backend-filtered data:', {
        totalMessages: messagesData.length,
        totalUsers: userData.length,
        dateRange: dateRange.map(d => d.toISOString()),
        accountTypeFilter: accountTypeFilter,
        note: 'Backend API already filtered by date range'
      });

      // Filter data based on account type selection only (exclude testing users from analysis)
      // Date filtering is already handled by backend API
      const filteredUserData = filterForAnalysis(userData);
      const filteredMessagesData = filterForAnalysis(messagesData);

      // Debug: Check filtering results
      if (accountTypeFilter) {
        console.log('Debug - Account Type Filtering:', {
          filter: accountTypeFilter,
          originalMessages: messagesData.length,
          filteredMessages: filteredMessagesData.length,
          originalUsers: userData.length,
          filteredUsers: filteredUserData.length
        });

        if (accountTypeFilter.includes('Spanish')) {
          const spanishSample = filteredMessagesData.slice(0, 5).map(msg => ({
            id: msg.id,
            user_id: msg.user_id,
            chat_language: msg.chat_language,
            user_languages: msg.user_languages,
            determined_type: determineAccountType(msg)
          }));
          console.log('Debug - Filtered Spanish messages sample:', spanishSample);
        }
      }

      const totalUsers = filteredUserData.length || 0;
      const totalMessages = filteredMessagesData.length || 0;

      // Calculate true average questions per user from the filtered data
      const avgQuestionsPerUser = totalUsers > 0 ? (totalMessages / totalUsers).toFixed(1) : '0';

      // Calculate average response time using only filtered messages that include a valid response_time
      const avgResponseTime =
        filteredUserData.length
          ? (filteredUserData.reduce((s, u) => s + parseFloat(u.avg_response_time), 0) /
            filteredUserData.length).toFixed(1)
          : '0';

      // Calculate user counts by account type
      const userTypeCounts = {
        'Internal': 0,
        'External (English)': 0,
        'External (Spanish)': 0,
        'External (English & Spanish)': 0,
        'Testing': 0
      };
      const messageTypeCounts = {
        'Internal': 0,
        'External (English)': 0,
        'External (Spanish)': 0,
        'External (English & Spanish)': 0,
        'Testing': 0
      };

      // Count users by type and calculate response times by type
      let internalResponseTimeSum = 0;
      let externalEnglishResponseTimeSum = 0;
      let externalSpanishResponseTimeSum = 0;
      let externalBilingualResponseTimeSum = 0;
      let testingResponseTimeSum = 0;
      let internalResponseTimeCount = 0;
      let externalEnglishResponseTimeCount = 0;
      let externalSpanishResponseTimeCount = 0;
      let externalBilingualResponseTimeCount = 0;
      let testingResponseTimeCount = 0;

      console.log('Processing user data:', filteredUserData.length, 'filtered users');
      console.log('Sample user data:', filteredUserData.slice(0, 3));

      // Debug: Check account type distribution in raw data
      const rawAccountTypes = userData.map(user => ({
        user_id: user.user_id,
        backend_account_type: user.account_type,
        determined_account_type: determineAccountType(user),
        is_testing: isTestingUser(user),
        will_be_filtered: !filterForAnalysis([user]).length
      }));
      console.log('Debug - Account Type Analysis:', {
        total_raw_users: userData.length,
        total_filtered_users: filteredUserData.length,
        sample_account_types: rawAccountTypes.slice(0, 10),
        internal_users_raw: userData.filter(u => u.account_type?.toLowerCase() === 'internal').length,
        internal_users_determined: userData.filter(u => determineAccountType(u) === 'Internal').length,
        internal_users_after_filter: filteredUserData.filter(u => determineAccountType(u) === 'Internal').length
      });

      filteredUserData.forEach(user => {
        const accountType = determineAccountType(user);
        userTypeCounts[accountType] = (userTypeCounts[accountType] || 0) + 1;

        // Calculate response time sums for each type
        const responseTime = parseFloat(user.avg_response_time);
        if (!isNaN(responseTime)) {
          if (accountType === 'Internal') {
            internalResponseTimeSum += responseTime;
            internalResponseTimeCount++;
          } else if (accountType === 'External (English)') {
            externalEnglishResponseTimeSum += responseTime;
            externalEnglishResponseTimeCount++;
          } else if (accountType === 'External (Spanish)') {
            externalSpanishResponseTimeSum += responseTime;
            externalSpanishResponseTimeCount++;
          } else if (accountType === 'External (English & Spanish)') {
            externalBilingualResponseTimeSum += responseTime;
            externalBilingualResponseTimeCount++;
            // Also add to both English and Spanish totals for filtering purposes
            externalEnglishResponseTimeSum += responseTime;
            externalEnglishResponseTimeCount++;
            externalSpanishResponseTimeSum += responseTime;
            externalSpanishResponseTimeCount++;
          } else if (accountType === 'Testing') {
            testingResponseTimeSum += responseTime;
            testingResponseTimeCount++;
          }
        }
      });

      console.log('User type counts:', userTypeCounts);
      console.log('Processing message data:', filteredMessagesData.length, 'filtered messages');
      console.log('Sample message data:', filteredMessagesData.slice(0, 3));

      // Debug: Check if chat_language field exists in message data
      const sampleMessages = filteredMessagesData.slice(0, 10);
      const languageFieldCheck = sampleMessages.map(msg => ({
        id: msg.id || 'no_id',
        chat_language: msg.chat_language,
        has_chat_language: 'chat_language' in msg,
        all_fields: Object.keys(msg)
      }));
      console.log('Debug - Message Language Field Check:', languageFieldCheck);

      // Count messages by user type using filtered data to ensure consistency
      filteredMessagesData.forEach(msg => {
        const accountType = determineAccountType(msg);
        messageTypeCounts[accountType] = (messageTypeCounts[accountType] || 0) + 1;
      });

      console.log('Message type counts:', messageTypeCounts);

      // Debug: Check Spanish message detection
      const spanishMessages = filteredMessagesData.filter(msg => {
        const accountType = determineAccountType(msg);
        return accountType === 'External (Spanish)' || accountType === 'External (English & Spanish)';
      });
      console.log('Debug - Spanish messages found:', {
        total: spanishMessages.length,
        sample: spanishMessages.slice(0, 5).map(msg => ({
          id: msg.id,
          user_id: msg.user_id,
          chat_language: msg.chat_language,
          user_languages: msg.user_languages,
          account_type: determineAccountType(msg)
        }))
      });

      // Calculate average questions per user type
      const avgQuestionsPerInternal = userTypeCounts['Internal'] > 0
        ? (messageTypeCounts['Internal'] / userTypeCounts['Internal']).toFixed(1)
        : '0';
      const avgQuestionsPerExternalEnglish = userTypeCounts['External (English)'] > 0
        ? (messageTypeCounts['External (English)'] / userTypeCounts['External (English)']).toFixed(1)
        : '0';
      const avgQuestionsPerExternalSpanish = userTypeCounts['External (Spanish)'] > 0
        ? (messageTypeCounts['External (Spanish)'] / userTypeCounts['External (Spanish)']).toFixed(1)
        : '0';
      const avgQuestionsPerExternalBilingual = userTypeCounts['External (English & Spanish)'] > 0
        ? (messageTypeCounts['External (English & Spanish)'] / userTypeCounts['External (English & Spanish)']).toFixed(1)
        : '0';
      const avgQuestionsPerTesting = userTypeCounts['Testing'] > 0
        ? (messageTypeCounts['Testing'] / userTypeCounts['Testing']).toFixed(1)
        : '0';

      // Calculate average response time per user type (excluding double-counted bilingual users)
      const avgResponseTimeInternal = internalResponseTimeCount > 0
        ? (internalResponseTimeSum / internalResponseTimeCount).toFixed(1)
        : '0';
      const avgResponseTimeExternalEnglish = (externalEnglishResponseTimeCount - externalBilingualResponseTimeCount) > 0
        ? ((externalEnglishResponseTimeSum - externalBilingualResponseTimeSum) / (externalEnglishResponseTimeCount - externalBilingualResponseTimeCount)).toFixed(1)
        : '0';
      const avgResponseTimeExternalSpanish = (externalSpanishResponseTimeCount - externalBilingualResponseTimeCount) > 0
        ? ((externalSpanishResponseTimeSum - externalBilingualResponseTimeSum) / (externalSpanishResponseTimeCount - externalBilingualResponseTimeCount)).toFixed(1)
        : '0';
      const avgResponseTimeExternalBilingual = externalBilingualResponseTimeCount > 0
        ? (externalBilingualResponseTimeSum / externalBilingualResponseTimeCount).toFixed(1)
        : '0';
      const avgResponseTimeTesting = testingResponseTimeCount > 0
        ? (testingResponseTimeSum / testingResponseTimeCount).toFixed(1)
        : '0';

      const newStatisticsData = {
        totalUsers: activeTab === 'Messages' ? totalMessages : totalUsers,
        avgQuestionsPerUser,
        avgResponseTime,
        internalUsers: userTypeCounts['Internal'],
        externalEnglishUsers: userTypeCounts['External (English)'],
        externalSpanishUsers: userTypeCounts['External (Spanish)'],
        externalBilingualUsers: userTypeCounts['External (English & Spanish)'],
        testingUsers: userTypeCounts['Testing'],
        // Add message breakdowns for tooltips
        internalMessages: messageTypeCounts['Internal'],
        externalEnglishMessages: messageTypeCounts['External (English)'],
        externalSpanishMessages: messageTypeCounts['External (Spanish)'],
        externalBilingualMessages: messageTypeCounts['External (English & Spanish)'],
        testingMessages: messageTypeCounts['Testing'],
        avgQuestionsPerInternal,
        avgQuestionsPerExternalEnglish,
        avgQuestionsPerExternalSpanish,
        avgQuestionsPerExternalBilingual,
        avgQuestionsPerTesting,
        avgResponseTimeInternal,
        avgResponseTimeExternalEnglish,
        avgResponseTimeExternalSpanish,
        avgResponseTimeExternalBilingual,
        avgResponseTimeTesting
      };

      console.log('🔄 Setting new statistics data:', {
        mainMetrics: {
          totalUsers: newStatisticsData.totalUsers,
          avgQuestionsPerUser: newStatisticsData.avgQuestionsPerUser,
          avgResponseTime: newStatisticsData.avgResponseTime
        },
        userBreakdown: {
          internal: newStatisticsData.internalUsers,
          externalEnglish: newStatisticsData.externalEnglishUsers,
          externalSpanish: newStatisticsData.externalSpanishUsers
        },
        timestamp: new Date().toISOString()
      });

      setStatisticsData(newStatisticsData);
    } catch (error) {
      console.error('Error processing statistics:', error);
    } finally {
      setProcessing(prev => ({ ...prev, statistics: false }));
    }
  }, [userData, messagesData, activeTab, accountTypeFilter, dateRange]);

  // Process Daily User Counts (for Users tab - Stacked Bar Chart)
  useEffect(() => {
    if (activeTab !== 'Users' || !selectedClient ||
      (selectedClient.key !== 'heritage' && selectedClient.key !== 'heritagepool') ||
      !messagesData || !userData) {
      setDailyUserCounts([]);
      return;
    }

    setProcessing(prev => ({ ...prev, dailyUsers: true }));

    try {
      // Build a mapping from user_id to account_type using ALL userData (not filtered) but exclude test users for analysis
      const userAccountMap = {};
      userData.forEach((u) => {
        if (u.user_id !== undefined) {
          const userId = u.user_id || u.email || u.id;
          // Skip if no user ID or invalid email format
          if (!userId) return;

          const userIdStr = String(userId);
          if (!userIdStr.includes('@')) {
            return;
          }

          // Skip testing users for daily counts analysis
          if (isTestingUser(u)) {
            return;
          }

          userAccountMap[u.user_id] = determineAccountType(u);
        }
      });

      // Aggregate unique users per date and account type using Sets
      const countsMap = {};

      messagesData.forEach((msg) => {
        const ts = msg.time_stamp || msg.created_at;
        if (!ts) return;

        const dateObj = new Date(ts);
        if (isNaN(dateObj)) return;

        const dateKey = formatLocalDateKey(dateObj);

        if (!countsMap[dateKey]) {
          countsMap[dateKey] = {
            'Internal': new Set(),
            'External (English)': new Set(),
            'External (Spanish)': new Set(),
            'External (English & Spanish)': new Set()
          };
        }

        const userId = msg.user_id;

        // Skip if no user ID or invalid email format
        if (!userId) return;

        const userIdStr = String(userId);
        if (!userIdStr.includes('@')) {
          return;
        }

        // Skip testing users for daily counts analysis
        if (isTestingUser(msg)) {
          return;
        }

        const typeFromMsg = determineAccountType(msg);
        const accountType = userAccountMap[userId] || typeFromMsg;

        // Add user to their determined account type
        countsMap[dateKey][accountType].add(userId);
      });

      // Generate all dates in the range to ensure continuity
      const generateDateRange = (startDate, endDate) => {
        const dates = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
          dates.push(formatLocalDateKey(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
      };

      // Convert to chart data format with all dates included
      const allDates = generateDateRange(dateRange[0], dateRange[1]);
      const chartData = allDates.map(dateKey => {
        // For the stacked bar chart, we need to avoid double-counting bilingual users
        // Bilingual users should be included in both English and Spanish counts for display
        const internal = countsMap[dateKey] ? countsMap[dateKey]['Internal'].size : 0;
        const englishOnly = countsMap[dateKey] ? countsMap[dateKey]['External (English)'].size : 0;
        const spanishOnly = countsMap[dateKey] ? countsMap[dateKey]['External (Spanish)'].size : 0;
        const bilingual = countsMap[dateKey] ? countsMap[dateKey]['External (English & Spanish)'].size : 0;

        if (accountTypeFilter) {
          // When filter is applied, only show the selected account type
          if (accountTypeFilter === 'Internal') {
            return {
              date: dateKey,
              'Internal': internal
            };
          } else if (accountTypeFilter === 'External (English)') {
            return {
              date: dateKey,
              'External (English)': englishOnly + bilingual // Include bilingual users
            };
          } else if (accountTypeFilter === 'External (Spanish)') {
            return {
              date: dateKey,
              'External (Spanish)': spanishOnly + bilingual // Include bilingual users
            };
          }
        }

        // No filter - show all account types
        return {
          date: dateKey,
          'Internal': internal,
          'External (English)': englishOnly + bilingual, // Include bilingual in English
          'External (Spanish)': spanishOnly + bilingual  // Include bilingual in Spanish
        };
      });



      setDailyUserCounts(chartData);
    } catch (error) {
      console.error('Error processing daily user counts:', error);
      setDailyUserCounts([]);
    } finally {
      setProcessing(prev => ({ ...prev, dailyUsers: false }));
    }
  }, [messagesData, userData, accountTypeFilter, activeTab, selectedClient, dateRange]);

  // Process Daily Message Counts (for Messages tab - Line Chart)
  useEffect(() => {
    if (activeTab !== 'Messages' || !selectedClient ||
      (selectedClient.key !== 'heritage' && selectedClient.key !== 'heritagepool') ||
      !messagesData) {
      setDailyMessageCounts([]);
      return;
    }

    setProcessing(prev => ({ ...prev, dailyMessages: true }));

    try {
      // Filter data based on account type selection only (exclude testing users from analysis)
      // Date filtering is already handled by backend API
      const filteredMessagesData = filterForAnalysis(messagesData);

      // Aggregate message counts per date and account type
      const countsMap = {};

      filteredMessagesData.forEach((msg) => {
        const ts = msg.time_stamp || msg.created_at;
        if (!ts) return;

        const dateObj = new Date(ts);
        if (isNaN(dateObj)) return;

        const dateKey = formatLocalDateKey(dateObj);
        const accountType = determineAccountType(msg);

        if (!countsMap[dateKey]) {
          countsMap[dateKey] = {
            'Internal': 0,
            'External (English)': 0,
            'External (Spanish)': 0,
            'External (English & Spanish)': 0
          };
        }

        countsMap[dateKey][accountType]++;
      });



      // Generate all dates in the range to ensure continuity using UTC dates
      const generateDateRange = (startDate, endDate) => {
        const dates = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
          dates.push(formatLocalDateKey(currentDate));
          currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }

        return dates;
      };

      // Convert to chart data format with all dates included
      const allDates = generateDateRange(dateRange[0], dateRange[1]);



      const chartData = allDates.map(dateKey => {
        if (!accountTypeFilter) {
          // No filter - show all categories
          return {
            date: dateKey,
            'Internal': countsMap[dateKey] ? countsMap[dateKey]['Internal'] : 0,
            'External (English)': countsMap[dateKey] ? countsMap[dateKey]['External (English)'] : 0,
            'External (Spanish)': countsMap[dateKey] ? countsMap[dateKey]['External (Spanish)'] : 0,
            'External (English & Spanish)': countsMap[dateKey] ? countsMap[dateKey]['External (English & Spanish)'] : 0
          };
        } else {
          // Filter applied - only show the filtered category with total count
          const totalCount = countsMap[dateKey] ? Object.values(countsMap[dateKey]).reduce((sum, count) => sum + count, 0) : 0;
          return {
            date: dateKey,
            [accountTypeFilter]: totalCount
          };
        }
      });

      setDailyMessageCounts(chartData);
    } catch (error) {
      console.error('Error processing daily message counts:', error);
      setDailyMessageCounts([]);
    } finally {
      setProcessing(prev => ({ ...prev, dailyMessages: false }));
    }
  }, [messagesData, activeTab, selectedClient, accountTypeFilter]);

  // Process Query Categories (for Messages tab - Pie Chart)
  useEffect(() => {
    if (activeTab !== 'Messages' || !selectedClient ||
      (selectedClient.key !== 'heritage' && selectedClient.key !== 'heritagepool') ||
      !messagesData) {
      setQueryCategories([]);
      return;
    }

    setProcessing(prev => ({ ...prev, queryCategories: true }));

    try {
      // Filter to external users only (backend already filtered by date range)
      const externalMessages = messagesData.filter(msg => {
        // Check both message account_type and metadata user_type
        const messageAccountType = (msg.account_type || '').toLowerCase();
        const metadataUserType = (msg.metadata?.user_type || '').toLowerCase();

        // Consider a message external only if both indicators confirm it's external
        // or if at least one indicates external and the other is not specified
        const isExternal =
          (messageAccountType === 'external' || !messageAccountType) &&
          (metadataUserType === 'external' || !metadataUserType) &&
          (messageAccountType === 'external' || metadataUserType === 'external');

        return isExternal;
      });

      console.log('Filtered external messages:', {
        total: messagesData.length,
        external: externalMessages.length,
        note: 'Using strict external user filtering'
      });

      // Category mappings for new query_category values (primary source)
      const NEW_CATEGORY_LABELS = {
        product_catalog: "Product Catalog",
        product_comparison: "Product Comparison",
        product_maintenance: "Product Maintenance",
        compatible_parts: "Compatible & Replacement Parts",
        product_manuals: "Product Manuals",
        pricing_inventory: "Pricing & Inventory",
        branch_availability: "Branch Availability",
        tutorial_videos: "Tutorial Videos",
        branch_information: "Branch Information",
        invoice_payment: "Invoice & Payment",
        orders: "Orders",
        nearest_branch: "Nearest Branch",
        general_conversation: "General Conversation",
        out_of_scope: "Out of Scope",
      };

      // Legacy category mapping for orchestrator metadata (fallback)
      const LEGACY_CATEGORY_KEY = {
        "1_1a": "Product Catalog",
        "1_1b": "Product Comparison",
        "1_1c": "Product Maintenance",
        "1_2a": "Compatible & Replacement Parts",
        "1_2b": "Product Manuals",
        "1_3a": "Product Availability & Pricing",
        "1_3b": "Nearest Branch Availability",
        "1_4": "Out of Scope Product Queries",
        "2_1": "Tutorial Videos",
        "2_2": "Branch Information",
        "2_3": "Invoice & Payment",
        "2_4": "Orders",
        "2_5": "Nearest Branch",
        "2_6": "Reserved",
        "2_7": "Reserved",
        "2_8": "Out of Scope Service Queries",
        "3": "General Inquiries",
        "4": "Out of Scope",
      };

      const determineCategoryLabel = (message) => {
        const metadata = message.metadata || {};
        const rawCategory = (message.query_category || metadata.query_category || '')
          .toString()
          .trim()
          .toLowerCase();

        if (rawCategory) {
          const normalized = NEW_CATEGORY_LABELS[rawCategory];
          if (normalized) {
            return normalized;
          }
        }

        const primary = metadata.primary_orchestrator;
        const secondary = metadata.secondary_orchestrator;

        let categoryKey;
        if (primary === '1' && secondary) {
          categoryKey = `1_${secondary}`;
        } else if (primary === '2' && secondary) {
          categoryKey = `2_${secondary}`;
        } else if (primary === '3') {
          categoryKey = '3';
        } else if (primary === '4') {
          categoryKey = '4';
        }

        if (categoryKey && LEGACY_CATEGORY_KEY[categoryKey]) {
          return LEGACY_CATEGORY_KEY[categoryKey];
        }

        if (rawCategory) {
          return rawCategory
            .split('_')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
        }

        return NEW_CATEGORY_LABELS.out_of_scope;
      };

      // Count messages by category using query_category with legacy fallback
      const categories = {};
      externalMessages.forEach(msg => {
        try {
          const label = determineCategoryLabel(msg);
          categories[label] = (categories[label] || 0) + 1;
        } catch (error) {
          console.error('Error processing message category:', error);
          categories[NEW_CATEGORY_LABELS.out_of_scope] = (categories[NEW_CATEGORY_LABELS.out_of_scope] || 0) + 1;
        }
      });

      // Convert to percentage-based data for pie chart
      const total = externalMessages.length;
      const pieData = Object.entries(categories)
        .map(([name, count]) => ({
          name,
          count,
          percentage: total > 0 ? Math.round((count / total) * 100) : 0
        }))
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count);

      setQueryCategories(pieData);
    } catch (error) {
      console.error('Error processing query categories:', error);
      setQueryCategories([]);
    } finally {
      setProcessing(prev => ({ ...prev, queryCategories: false }));
    }
  }, [messagesData, activeTab, selectedClient]);

  // Process Feedback Data
  useEffect(() => {
    if (!messagesData || !userData) {
      setFeedbackData({ total: 0, positive: 0, negative: 0 });
      return;
    }

    setProcessing(prev => ({ ...prev, feedback: true }));

    try {
      // Filter data based on account type selection (exclude testing users from feedback analysis)
      const filteredMessagesData = filterForAnalysis(messagesData);

      // Calculate feedback from messages data
      let totalFromMessages = 0;
      let positiveFromMessages = 0;
      let negativeFromMessages = 0;

      console.log('Processing feedback from messages:', filteredMessagesData.length, 'messages');

      filteredMessagesData.forEach(msg => {
        // Testing users are already excluded by filterForAnalysis

        if (msg.user_feedback !== null && msg.user_feedback !== undefined && msg.user_feedback !== '') {
          const feedbackValue = Number(msg.user_feedback);
          if (!isNaN(feedbackValue)) {
            totalFromMessages++;
            if (feedbackValue === 1) {
              positiveFromMessages++;
            } else if (feedbackValue === -1) {
              negativeFromMessages++;
            }
          }
        }
      });

      console.log('Feedback counts:', {
        total: totalFromMessages,
        positive: positiveFromMessages,
        negative: negativeFromMessages
      });

      setFeedbackData({
        total: totalFromMessages,
        positive: positiveFromMessages,
        negative: negativeFromMessages
      });
    } catch (error) {
      console.error('Error processing feedback data:', error);
      setFeedbackData({ total: 0, positive: 0, negative: 0 });
    } finally {
      setProcessing(prev => ({ ...prev, feedback: false }));
    }
  }, [messagesData, userData, accountTypeFilter]);

  // Export filtered data for table usage (excludes testing users from display)
  const filteredMessagesData = useMemo(() => {
    if (!messagesData) return [];
    const filtered = filterForDisplay(messagesData);

    return filtered;
  }, [messagesData, accountTypeFilter]);

  const filteredUserData = useMemo(() => {
    if (!userData) return [];
    const filtered = filterForDisplay(userData);
    console.log('Memoized filteredUserData:', {
      original: userData.length,
      filtered: filtered.length,
      accountTypeFilter
    });
    return filtered;
  }, [userData, accountTypeFilter]);

  // Export analysis counts (excludes testing users) for tab buttons
  const analysisMessagesData = useMemo(() => {
    if (!messagesData) return [];
    return filterForAnalysis(messagesData);
  }, [messagesData, accountTypeFilter]);

  const analysisUserData = useMemo(() => {
    if (!userData) return [];
    return filterForAnalysis(userData);
  }, [userData, accountTypeFilter]);

  return {
    // Processed Data
    statisticsData,
    dailyMessageCounts,
    dailyUserCounts,
    queryCategories,
    feedbackData,

    // Filtered Data for Tables (excludes testing users from display)
    filteredMessagesData,
    filteredUserData,

    // Filtered Data for Analysis (excludes testing users for tab counts)
    analysisMessagesData,
    analysisUserData,

    // Processing States
    processing,

    // Computed Loading State
    loading: Object.values(processing).some(Boolean)
  };
}; 
